// График отгрузки: конверсии между моделью бэка (YearSchedule.days = number[][], индекс месяца 0–11)
// и плоским списком ISO-дат «yyyy-mm-dd». Работаем строками (без Date/таймзон), как в lib/date.ts.
import { addDaysIso, ruToIso } from '@/lib/date';
import type { ShipmentSchedule, YearSchedule } from '@/types/deals';

const pad = (n: number): string => String(n).padStart(2, '0');

/** YearSchedule[] → плоский список ISO-дат выбранных дней. days[mi]=[номера дней], mi — месяц 0–11. */
export function scheduleToIso(dates: readonly YearSchedule[] | undefined): string[] {
  const out: string[] = [];
  for (const ys of dates ?? []) {
    const year = ys.year;
    if (year == null) {
      continue;
    }
    (ys.days ?? []).forEach((daysOfMonth, monthIndex) => {
      for (const day of daysOfMonth ?? []) {
        out.push(`${year}-${pad(monthIndex + 1)}-${pad(day)}`);
      }
    });
  }
  return out.sort();
}

/** Плоский список ISO-дат → YearSchedule[] (группировка по году, days как 12 массивов). */
export function isoToSchedule(isoDates: readonly string[]): YearSchedule[] {
  const byYear = new Map<number, number[][]>();
  for (const iso of isoDates) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) {
      continue;
    }
    const year = Number(m[1]);
    const monthIndex = Number(m[2]) - 1;
    const day = Number(m[3]);
    if (!byYear.has(year)) {
      byYear.set(year, Array.from({ length: 12 }, () => [] as number[]));
    }
    byYear.get(year)![monthIndex]!.push(day);
  }
  return [...byYear.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([year, days]) => ({ year, days: days.map((d) => [...d].sort((x, y) => x - y)) }));
}

export interface CalendarMonth {
  readonly year: number;
  /** 1–12. */
  readonly month: number;
}

/** Список месяцев (год+месяц), пересекающих диапазон [minIso, maxIso] включительно. */
export function monthsInRange(minIso: string, maxIso: string): CalendarMonth[] {
  const [minY, minM] = minIso.split('-').map(Number);
  const [maxY, maxM] = maxIso.split('-').map(Number);
  const out: CalendarMonth[] = [];
  let y = minY!;
  let m = minM!;
  // Жёсткий предел итераций — страховка от некорректного диапазона.
  for (let guard = 0; guard < 36; guard += 1) {
    out.push({ year: y, month: m });
    if (y === maxY && m === maxM) {
      break;
    }
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/** Количество дней в месяце (month 1–12). */
export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** День недели 1-го числа месяца, понедельник=0 … воскресенье=6 (русская неделя). */
export function firstWeekdayMon(year: number, month: number): number {
  return (new Date(year, month - 1, 1).getDay() + 6) % 7;
}

/** ISO дня в сетке месяца. */
export function dayIso(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Диапазон выбора графика по данным сделки: [start, start+requiredDays+30] (как валидирует бэк). */
export function scheduleRange(startRu: string | undefined, requiredDays: number): { minIso: string; maxIso: string } | null {
  const minIso = ruToIso(startRu);
  if (!minIso) {
    return null;
  }
  return { minIso, maxIso: addDaysIso(minIso, Math.max(requiredDays, 1) + 30) };
}

export interface SchedulePeriod {
  /** ISO первого дня отгрузки. */
  readonly startIso: string;
  /** ISO последнего (по факту выбора) дня отгрузки. */
  readonly endIso: string;
  /** Кол-во выбранных дней отгрузки. */
  readonly days: number;
}

/** Фактический период согласованного графика: первый/последний выбранный день + число дней.
 *  Последний день берём из выбранных дат (поле `end` бэка часто пустое); null — дат нет. */
export function schedulePeriod(schedule: ShipmentSchedule | undefined): SchedulePeriod | null {
  const isos = scheduleToIso(schedule?.dates);
  if (isos.length === 0) {
    return null;
  }
  // scheduleToIso возвращает отсортированный список — первый/последний берём с краёв.
  const startIso = ruToIso(schedule?.start) ?? isos[0]!;
  const endIso = ruToIso(schedule?.end) ?? isos[isos.length - 1]!;
  return { startIso, endIso, days: isos.length };
}
